import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  getEvents,
  registerForEvent,
  unregisterFromEvent,
  getMyEventRegistrations,
  resolveAssetUrl,
} from '../api/api';
import { useAuth } from '../context/AuthContext';

const statusColors = {
  upcoming: { bg: '#d1fae5', color: '#065f46' },
  completed: { bg: '#e0e7ff', color: '#3730a3' },
  cancelled: { bg: '#fee2e2', color: '#dc2626' },
};

const NAV_ITEMS = [
  { icon: 'calendar-event', key: 'Events', section: 'events' },
  { icon: 'bookmark-check', key: 'MyEvents', section: 'myevents' },
  { icon: 'person', key: 'Profile', section: 'profile' },
];

export default function MemberDashboard() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const { i18n } = useTranslation();
  // Member dashboard is always Arabic / RTL regardless of the site language
  const t = i18n.getFixedT('ar');
  const isRTL = true;

  const [activeSection, setActiveSection] = useState('events');
  const [events, setEvents] = useState([]);
  const [myRegs, setMyRegs] = useState([]);
  const [registeredIds, setRegisteredIds] = useState(new Set());
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);
  const [detailEvent, setDetailEvent] = useState(null); // event shown in the details dialog

  useEffect(() => {
    // Fetch independently so a failure in one (e.g. an expired token on the
    // registrations call) never blanks out the public events list.
    getEvents()
      .then((ev) => setEvents(ev.data))
      .catch(() => {})
      .finally(() => setLoading(false));

    getMyEventRegistrations()
      .then((regs) => {
        setMyRegs(regs.data);
        setRegisteredIds(new Set(regs.data.map((r) => r.event?.id).filter(Boolean)));
      })
      .catch(() => {});
  }, []);

  const refreshRegs = async () => {
    const regs = await getMyEventRegistrations();
    setMyRegs(regs.data);
    setRegisteredIds(new Set(regs.data.map((r) => r.event?.id).filter(Boolean)));
  };

  const handleRegister = async (eventId) => {
    setBusyId(eventId);
    try {
      await registerForEvent(eventId);
      await refreshRegs();
    } catch (err) {
      window.alert(err.response?.data?.message || 'Error');
    } finally {
      setBusyId(null);
    }
  };

  const handleUnregister = async (eventId) => {
    setBusyId(eventId);
    try {
      await unregisterFromEvent(eventId);
      await refreshRegs();
    } finally {
      setBusyId(null);
    }
  };

  const statusLabel = (s) =>
    ({
      upcoming: t('Home.Admin.Upcoming'),
      completed: t('Home.Admin.Completed'),
      cancelled: t('Home.Admin.Cancelled'),
    }[s] || s);

  const formatDate = (d) =>
    new Date(d).toLocaleDateString('ar', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });

  return (
    <div style={{ minHeight: '100vh', background: '#f1f5f9', direction: isRTL ? 'rtl' : 'ltr' }}>
      <div style={{ display: 'flex', minHeight: '100vh' }}>
        {/* ── Sidebar ── */}
        <div
          style={{
            width: '240px',
            background: 'linear-gradient(180deg, #1a1a2e 0%, #0f3460 100%)',
            color: 'white',
            display: 'flex',
            flexDirection: 'column',
            flexShrink: 0,
          }}
        >
          <div style={{ padding: '28px 24px 20px' }}>
            <img src="/img/logo.png" alt="ENGPI" height="45" />
            <div style={{ fontSize: '11px', opacity: 0.5, marginTop: '8px', letterSpacing: '1px' }}>
              {t('Home.Admin.MemberNav')}
            </div>
          </div>

          <nav style={{ flex: 1, padding: '8px 12px' }}>
            {NAV_ITEMS.map((item) => {
              const isActive = activeSection === item.section;
              return (
                <div
                  key={item.section}
                  onClick={() => setActiveSection(item.section)}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '10px',
                    padding: '10px 12px',
                    borderRadius: '8px',
                    background: isActive ? 'rgba(255,255,255,0.12)' : 'transparent',
                    cursor: 'pointer',
                    fontSize: '14px',
                    fontWeight: isActive ? 600 : 400,
                    marginBottom: '4px',
                    transition: 'background 0.15s',
                    userSelect: 'none',
                  }}
                  onMouseEnter={(e) => { if (!isActive) e.currentTarget.style.background = 'rgba(255,255,255,0.07)'; }}
                  onMouseLeave={(e) => { if (!isActive) e.currentTarget.style.background = 'transparent'; }}
                >
                  <i className={`bi bi-${item.icon}`} />
                  {t(`Home.Admin.${item.key}`)}
                </div>
              );
            })}
            <div
              onClick={() => navigate('/')}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '10px',
                padding: '10px 12px',
                borderRadius: '8px',
                cursor: 'pointer',
                fontSize: '14px',
                marginTop: '4px',
                opacity: 0.75,
                userSelect: 'none',
              }}
            >
              <i className="bi bi-house" />
              {t('Navbar.Home')}
            </div>
          </nav>

          <div style={{ padding: '16px 24px', borderTop: '1px solid rgba(255,255,255,0.1)' }}>
            <div style={{ fontSize: '13px', opacity: 0.7 }}>{user?.fullName}</div>
            <div style={{ fontSize: '11px', opacity: 0.45, marginTop: '2px' }}>{user?.email}</div>
            <button
              onClick={() => { logout(); navigate('/login'); }}
              style={{
                marginTop: '12px',
                background: 'rgba(255,255,255,0.1)',
                border: '1px solid rgba(255,255,255,0.2)',
                color: 'white',
                borderRadius: '6px',
                padding: '6px 12px',
                fontSize: '12px',
                cursor: 'pointer',
                width: '100%',
              }}
            >
              <i className="bi bi-box-arrow-left me-1" /> {t('Home.Admin.Logout')}
            </button>
          </div>
        </div>

        {/* ── Main ── */}
        <div style={{ flex: 1, padding: '32px', overflowY: 'auto' }}>
          <div style={{ marginBottom: '28px', textAlign: isRTL ? 'right' : 'left' }}>
            <h4 style={{ fontWeight: 700, margin: 0 }}>{t('Home.Admin.MemberDashboardTitle')}</h4>
            <p style={{ color: '#6c757d', margin: 0, fontSize: '14px' }}>
              {t('Home.Admin.WelcomeBack')}, {user?.fullName}
            </p>
          </div>

          {loading ? (
            <div className="text-center py-5">
              <div className="spinner-border text-warning" role="status" />
            </div>
          ) : (
            <>
              {/* Events */}
              {activeSection === 'events' && (
                <div>
                  <h6 style={{ fontWeight: 700, marginBottom: '16px' }}>{t('Home.Admin.AllEvents')}</h6>
                  {events.length === 0 ? (
                    <EmptyState icon="calendar-x" text={t('Home.Admin.NoEvents')} />
                  ) : (
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: '20px' }}>
                      {events.map((ev) => {
                        const isReg = registeredIds.has(ev.id);
                        const isFull = ev.capacity != null && ev.registrationCount >= ev.capacity;
                        const sc = statusColors[ev.status] || statusColors.upcoming;
                        return (
                          <div
                            key={ev.id}
                            style={{
                              background: 'white',
                              borderRadius: '12px',
                              boxShadow: '0 2px 12px rgba(0,0,0,0.06)',
                              overflow: 'hidden',
                              display: 'flex',
                              flexDirection: 'column',
                            }}
                          >
                            <div
                              style={{
                                height: '120px',
                                background: ev.coverImage
                                  ? `url(${resolveAssetUrl(ev.coverImage)}) center/cover`
                                  : 'linear-gradient(135deg, #1a1a2e, #0f3460)',
                                position: 'relative',
                              }}
                            >
                              <span
                                style={{
                                  position: 'absolute',
                                  top: '10px',
                                  [isRTL ? 'left' : 'right']: '10px',
                                  background: sc.bg,
                                  color: sc.color,
                                  padding: '3px 10px',
                                  borderRadius: '999px',
                                  fontSize: '11px',
                                  fontWeight: 600,
                                  textTransform: 'uppercase',
                                }}
                              >
                                {statusLabel(ev.status)}
                              </span>
                            </div>
                            <div style={{ padding: '16px', flex: 1, display: 'flex', flexDirection: 'column' }}>
                              <div
                                onClick={() => setDetailEvent(ev)}
                                style={{ cursor: 'pointer' }}
                                title={t('Home.Admin.ViewDetails')}
                              >
                                <h6 style={{ fontWeight: 700, margin: 0, fontSize: '15px' }}>{ev.title}</h6>
                                {ev.description && (
                                  <p
                                    style={{
                                      color: '#6c757d',
                                      fontSize: '13px',
                                      margin: '8px 0 0',
                                      display: '-webkit-box',
                                      WebkitLineClamp: 2,
                                      WebkitBoxOrient: 'vertical',
                                      overflow: 'hidden',
                                    }}
                                  >
                                    {ev.description}
                                  </p>
                                )}
                                <span
                                  style={{
                                    display: 'inline-block',
                                    marginTop: '8px',
                                    color: '#0f3460',
                                    fontSize: '12px',
                                    fontWeight: 600,
                                  }}
                                >
                                  {t('Home.Admin.ViewDetails')}
                                  <i className={`bi bi-chevron-${isRTL ? 'left' : 'right'} ${isRTL ? 'me-1' : 'ms-1'}`} style={{ fontSize: '10px' }} />
                                </span>
                              </div>
                              <div style={{ fontSize: '12px', color: '#374151', marginTop: '12px', display: 'flex', flexDirection: 'column', gap: '4px' }}>
                                <span><i className={`bi bi-clock ${isRTL ? 'ms-1' : 'me-1'}`} />{formatDate(ev.date)}</span>
                                {ev.location && <span><i className={`bi bi-geo-alt ${isRTL ? 'ms-1' : 'me-1'}`} />{ev.location}</span>}
                              </div>

                              <div style={{ marginTop: 'auto', paddingTop: '14px' }}>
                                {isReg ? (
                                  <button
                                    onClick={() => handleUnregister(ev.id)}
                                    disabled={busyId === ev.id}
                                    style={{
                                      width: '100%',
                                      padding: '8px',
                                      borderRadius: '8px',
                                      border: '1.5px solid #dc2626',
                                      background: 'white',
                                      color: '#dc2626',
                                      fontWeight: 600,
                                      fontSize: '13px',
                                      cursor: 'pointer',
                                    }}
                                  >
                                    <i className="bi bi-check-circle me-1" />
                                    {busyId === ev.id ? '…' : t('Home.Admin.UnregisterEvent')}
                                  </button>
                                ) : ev.status !== 'upcoming' ? (
                                  <button
                                    disabled
                                    style={{
                                      width: '100%', padding: '8px', borderRadius: '8px', border: 'none',
                                      background: '#f1f5f9', color: '#9ca3af', fontWeight: 600, fontSize: '13px',
                                    }}
                                  >
                                    {statusLabel(ev.status)}
                                  </button>
                                ) : ev.registrationOpen === false ? (
                                  <button
                                    disabled
                                    style={{
                                      width: '100%', padding: '8px', borderRadius: '8px', border: 'none',
                                      background: '#f1f5f9', color: '#9ca3af', fontWeight: 600, fontSize: '13px',
                                    }}
                                  >
                                    {t('Home.Admin.RegistrationClosed')}
                                  </button>
                                ) : isFull ? (
                                  <button
                                    disabled
                                    style={{
                                      width: '100%', padding: '8px', borderRadius: '8px', border: 'none',
                                      background: '#fef3c7', color: '#92400e', fontWeight: 600, fontSize: '13px',
                                    }}
                                  >
                                    {t('Home.Admin.EventFull')}
                                  </button>
                                ) : (
                                  <button
                                    onClick={() => handleRegister(ev.id)}
                                    disabled={busyId === ev.id}
                                    style={{
                                      width: '100%',
                                      padding: '8px',
                                      borderRadius: '8px',
                                      border: 'none',
                                      background: 'linear-gradient(135deg, #f5a623, #e8920c)',
                                      color: 'white',
                                      fontWeight: 600,
                                      fontSize: '13px',
                                      cursor: 'pointer',
                                    }}
                                  >
                                    {busyId === ev.id ? '…' : t('Home.Admin.RegisterEvent')}
                                  </button>
                                )}
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              )}

              {/* My Events */}
              {activeSection === 'myevents' && (
                <div>
                  <h6 style={{ fontWeight: 700, marginBottom: '16px' }}>{t('Home.Admin.MyEvents')}</h6>
                  {myRegs.length === 0 ? (
                    <EmptyState icon="bookmark-x" text={t('Home.Admin.NoMyEvents')} />
                  ) : (
                    <div
                      style={{
                        background: 'white',
                        borderRadius: '12px',
                        boxShadow: '0 2px 12px rgba(0,0,0,0.06)',
                        overflow: 'hidden',
                      }}
                    >
                      {myRegs.map((reg, i) => {
                        const ev = reg.event;
                        if (!ev) return null;
                        const sc = statusColors[ev.status] || statusColors.upcoming;
                        return (
                          <div
                            key={reg.id}
                            style={{
                              padding: '16px 24px',
                              borderTop: i === 0 ? 'none' : '1px solid #f1f5f9',
                              display: 'flex',
                              justifyContent: 'space-between',
                              alignItems: 'center',
                              gap: '16px',
                              flexDirection: isRTL ? 'row-reverse' : 'row',
                            }}
                          >
                            <div style={{ textAlign: isRTL ? 'right' : 'left' }}>
                              <div style={{ fontWeight: 600, fontSize: '14px' }}>{ev.title}</div>
                              <div style={{ fontSize: '12px', color: '#6c757d', marginTop: '4px' }}>
                                <i className={`bi bi-clock ${isRTL ? 'ms-1' : 'me-1'}`} />{formatDate(ev.date)}
                                {ev.location && <span style={{ marginInlineStart: '12px' }}><i className={`bi bi-geo-alt ${isRTL ? 'ms-1' : 'me-1'}`} />{ev.location}</span>}
                              </div>
                            </div>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                              <span
                                style={{
                                  background: sc.bg, color: sc.color, padding: '3px 10px',
                                  borderRadius: '999px', fontSize: '11px', fontWeight: 600, textTransform: 'uppercase',
                                }}
                              >
                                {statusLabel(ev.status)}
                              </span>
                              {ev.status === 'upcoming' && (
                                <button
                                  onClick={() => handleUnregister(ev.id)}
                                  disabled={busyId === ev.id}
                                  style={{
                                    padding: '5px 12px', borderRadius: '6px', background: '#fee2e2',
                                    color: '#dc2626', border: 'none', fontSize: '12px', fontWeight: 600, cursor: 'pointer',
                                  }}
                                >
                                  {busyId === ev.id ? '…' : t('Home.Admin.UnregisterEvent')}
                                </button>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              )}

              {/* Profile */}
              {activeSection === 'profile' && (
                <div
                  style={{
                    background: 'white',
                    borderRadius: '12px',
                    boxShadow: '0 2px 12px rgba(0,0,0,0.06)',
                    padding: '32px',
                    maxWidth: '520px',
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: '16px', marginBottom: '28px', flexDirection: isRTL ? 'row-reverse' : 'row' }}>
                    <div
                      style={{
                        width: '64px', height: '64px', borderRadius: '50%',
                        background: 'linear-gradient(135deg, #1a1a2e, #0f3460)',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        color: 'white', fontSize: '26px', fontWeight: 700,
                      }}
                    >
                      {(user?.fullName || '?')[0].toUpperCase()}
                    </div>
                    <div style={{ textAlign: isRTL ? 'right' : 'left' }}>
                      <div style={{ fontWeight: 700, fontSize: '18px' }}>{user?.fullName}</div>
                      <div style={{ color: '#6c757d', fontSize: '13px' }}>{user?.email}</div>
                    </div>
                  </div>

                  <ProfileRow label={t('Home.Admin.ProfileName')} value={user?.fullName} isRTL={isRTL} />
                  <ProfileRow label={t('Home.Admin.ProfileEmail')} value={user?.email} isRTL={isRTL} />
                  <ProfileRow
                    label={t('Home.Admin.ProfileRole')}
                    value={user?.role === 'admin' ? t('Home.Admin.AdminRole') : t('Home.Admin.MemberRole')}
                    isRTL={isRTL}
                  />
                  {user?.createdAt && (
                    <ProfileRow
                      label={t('Home.Admin.ProfileJoined')}
                      value={new Date(user.createdAt).toLocaleDateString('ar')}
                      isRTL={isRTL}
                    />
                  )}
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {/* ── Event details dialog ── */}
      {detailEvent && (() => {
        const ev = detailEvent;
        const isReg = registeredIds.has(ev.id);
        const isFull = ev.capacity != null && ev.registrationCount >= ev.capacity;
        const sc = statusColors[ev.status] || statusColors.upcoming;
        return (
          <div
            onClick={(e) => { if (e.target === e.currentTarget) setDetailEvent(null); }}
            style={{
              position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)',
              display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: '16px',
              direction: isRTL ? 'rtl' : 'ltr',
            }}
          >
            <div
              style={{
                background: 'white', borderRadius: '16px', width: '100%', maxWidth: '540px',
                maxHeight: '90vh', overflowY: 'auto', boxShadow: '0 20px 60px rgba(0,0,0,0.25)',
              }}
            >
              <div
                style={{
                  height: '180px', position: 'relative',
                  background: ev.coverImage
                    ? `url(${resolveAssetUrl(ev.coverImage)}) center/cover`
                    : 'linear-gradient(135deg, #1a1a2e, #0f3460)',
                  borderTopLeftRadius: '16px', borderTopRightRadius: '16px',
                }}
              >
                <button
                  onClick={() => setDetailEvent(null)}
                  style={{
                    position: 'absolute', top: '12px', [isRTL ? 'left' : 'right']: '12px',
                    width: '32px', height: '32px', borderRadius: '50%', border: 'none',
                    background: 'rgba(0,0,0,0.55)', color: 'white', cursor: 'pointer', fontSize: '18px', lineHeight: 1,
                  }}
                >
                  ×
                </button>
                <span
                  style={{
                    position: 'absolute', bottom: '12px', [isRTL ? 'right' : 'left']: '12px',
                    background: sc.bg, color: sc.color, padding: '4px 12px',
                    borderRadius: '999px', fontSize: '11px', fontWeight: 600, textTransform: 'uppercase',
                  }}
                >
                  {statusLabel(ev.status)}
                </span>
              </div>

              <div style={{ padding: '24px', textAlign: isRTL ? 'right' : 'left' }}>
                <h5 style={{ fontWeight: 700, margin: 0 }}>{ev.title}</h5>

                <div style={{ fontSize: '13px', color: '#374151', margin: '16px 0', display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  <span><i className={`bi bi-clock ${isRTL ? 'ms-2' : 'me-2'}`} />{formatDate(ev.date)}</span>
                  {ev.location && <span><i className={`bi bi-geo-alt ${isRTL ? 'ms-2' : 'me-2'}`} />{ev.location}</span>}
                </div>

                {ev.description && (
                  <p style={{ color: '#374151', fontSize: '14px', lineHeight: 1.7, whiteSpace: 'pre-wrap', wordBreak: 'break-word', margin: 0 }}>
                    {ev.description}
                  </p>
                )}

                <div style={{ marginTop: '24px' }}>
                  {isReg ? (
                    <button
                      onClick={() => handleUnregister(ev.id)}
                      disabled={busyId === ev.id}
                      style={{
                        width: '100%', padding: '10px', borderRadius: '8px',
                        border: '1.5px solid #dc2626', background: 'white', color: '#dc2626',
                        fontWeight: 600, fontSize: '14px', cursor: 'pointer',
                      }}
                    >
                      <i className="bi bi-check-circle me-1" />
                      {busyId === ev.id ? '…' : t('Home.Admin.UnregisterEvent')}
                    </button>
                  ) : ev.status !== 'upcoming' ? (
                    <button disabled style={{ width: '100%', padding: '10px', borderRadius: '8px', border: 'none', background: '#f1f5f9', color: '#9ca3af', fontWeight: 600, fontSize: '14px' }}>
                      {statusLabel(ev.status)}
                    </button>
                  ) : ev.registrationOpen === false ? (
                    <button disabled style={{ width: '100%', padding: '10px', borderRadius: '8px', border: 'none', background: '#f1f5f9', color: '#9ca3af', fontWeight: 600, fontSize: '14px' }}>
                      {t('Home.Admin.RegistrationClosed')}
                    </button>
                  ) : isFull ? (
                    <button disabled style={{ width: '100%', padding: '10px', borderRadius: '8px', border: 'none', background: '#fef3c7', color: '#92400e', fontWeight: 600, fontSize: '14px' }}>
                      {t('Home.Admin.EventFull')}
                    </button>
                  ) : (
                    <button
                      onClick={() => handleRegister(ev.id)}
                      disabled={busyId === ev.id}
                      style={{
                        width: '100%', padding: '10px', borderRadius: '8px', border: 'none',
                        background: 'linear-gradient(135deg, #f5a623, #e8920c)', color: 'white',
                        fontWeight: 600, fontSize: '14px', cursor: 'pointer',
                      }}
                    >
                      {busyId === ev.id ? '…' : t('Home.Admin.RegisterEvent')}
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>
        );
      })()}
    </div>
  );
}

function EmptyState({ icon, text }) {
  return (
    <div
      style={{
        background: 'white',
        borderRadius: '12px',
        boxShadow: '0 2px 12px rgba(0,0,0,0.06)',
        padding: '48px',
        textAlign: 'center',
        color: '#6c757d',
      }}
    >
      <i className={`bi bi-${icon}`} style={{ fontSize: '2.5rem' }} />
      <p className="mt-2 mb-0">{text}</p>
    </div>
  );
}

function ProfileRow({ label, value, isRTL }) {
  return (
    <div
      style={{
        display: 'flex',
        justifyContent: 'space-between',
        padding: '12px 0',
        borderBottom: '1px solid #f1f5f9',
        flexDirection: isRTL ? 'row-reverse' : 'row',
      }}
    >
      <span style={{ color: '#6c757d', fontSize: '13px' }}>{label}</span>
      <span style={{ fontWeight: 600, fontSize: '14px' }}>{value}</span>
    </div>
  );
}
